import { useTranslation } from 'react-i18next';
import { Button, RadioGroup, RadioGroupItem } from '@parrot-software/design-system';
import { Plus } from '@parrot-software/design-system/icons';
import type { InvoiceDataViewDto, SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { CustomerStates } from '@subscriptions/domain/types/SubscriptionsConst';
import { NO_INVOICING_VALUE } from '@subscriptions/presentation/constants';
import { useInvoiceData, useInvoiceDataActions, useInvoiceDataDeepLink } from '@subscriptions/presentation/hooks';
import { useAreSubscriptionActionsLocked } from '@subscriptions/presentation/hooks/useAreSubscriptionActionsLocked';
import { EmptyState, ErrorState } from '@subscriptions/presentation/components/base';
import { InvoiceDataFormSheet } from '@subscriptions/presentation/components/specific/InvoiceDataForm';
import type { InvoiceDataFormMode } from '@subscriptions/presentation/components/specific/InvoiceDataForm';
import {
  DeleteInvoiceDataDialog,
  InvoiceDataAssignedDialog,
  InvoiceDataCard,
  InvoiceDataListSkeleton,
  NoInvoicingConfirmationDialog,
} from '@subscriptions/presentation/components/specific/InvoiceDataList';

interface BlockInvoicingContentProps {
  block: SubscriptionBlockViewDto;
}

/**
 * BlockInvoicingContent - Invoicing tab for the block detail page.
 *
 * Displays the organization's invoice data as selectable cards.
 * Supports creating, editing, and deleting invoice data.
 * Delete is blocked when the invoice data is assigned to any subscription block.
 * `?invoiceDataUuid=<uuid>` opens the sheet on mount, in edit mode by default or in retry mode
 * when `?invoiceDataMode=retry` — see useInvoiceDataDeepLink.
 *
 * @param {Object} props - Component properties
 * @param {SubscriptionBlockViewDto} props.block - Current subscription block data
 * @returns {React.ReactElement} Block invoicing content
 */
export const BlockInvoicingContent = ({ block }: BlockInvoicingContentProps) => {
  const { t } = useTranslation('invoiceData');
  const { data: invoiceData, isLoading, isError, refetch } = useInvoiceData();

  const isInactive = block.customerState === CustomerStates.INACTIVE;
  const areActionsLocked = useAreSubscriptionActionsLocked();

  const {
    selectedValue,
    isSheetOpen,
    sheetMode,
    editingInvoiceData,
    retryErrorMessage,
    retryPaymentUuid,
    retryOnlyFailed,
    deletingInvoiceData,
    blockedInvoiceData,
    isNoInvoicingDialogOpen,
    isDeleting,
    isUpdating,
    handleValueChange,
    handleOpenSheetChange,
    handleSheetCloseComplete,
    handleCancelSheet,
    handleOpenCreate,
    handleEdit,
    handleRetry,
    handleDelete,
    handleConfirmDelete,
    handleCreateSuccess,
    handleEditSuccess,
    handleRetrySuccess,
    handleDeleteDialogChange,
    handleBlockedDialogChange,
    handleBlockedDialogClose,
    handleConfirmNoInvoicing,
    handleCancelNoInvoicing,
    handleNoInvoicingDialogChange,
  } = useInvoiceDataActions(block);

  // Each mode reports its own outcome: create assigns the record to the block, edit confirms
  // the save, retry confirms the payment it re-ran.
  const successHandlersByMode: Record<
    InvoiceDataFormMode,
    (invoiceData: InvoiceDataViewDto) => Promise<void> | void
  > = {
    create: handleCreateSuccess,
    edit: handleEditSuccess,
    retry: handleRetrySuccess,
  };

  useInvoiceDataDeepLink({
    activeUuid: isSheetOpen ? editingInvoiceData?.uuid : null,
    invoiceData,
    isEnabled: !isInactive,
    onEdit: handleEdit,
    onRetry: handleRetry,
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-1 flex-col gap-0.5">
          <h2 className="text-xl font-semibold">{t('blockInvoicing.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('blockInvoicing.subtitle')}</p>
        </div>
        <Button
          className="w-full md:w-auto"
          disabled={isInactive || areActionsLocked}
          onClick={handleOpenCreate}
          variant="secondary"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('blockInvoicing.addButton')}
        </Button>
      </div>

      <InvoiceDataFormSheet
        blockUuid={block.uuid}
        errorMessage={retryErrorMessage}
        initialValues={editingInvoiceData ?? undefined}
        mode={sheetMode}
        onCancel={handleCancelSheet}
        onCloseComplete={handleSheetCloseComplete}
        onOpenChange={handleOpenSheetChange}
        onSuccess={successHandlersByMode[sheetMode]}
        onlyFailed={retryOnlyFailed}
        open={isSheetOpen}
        paymentUuid={retryPaymentUuid}
      />

      <DeleteInvoiceDataDialog
        invoiceData={deletingInvoiceData ?? undefined}
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onOpenChange={handleDeleteDialogChange}
        open={Boolean(deletingInvoiceData)}
      />

      <InvoiceDataAssignedDialog
        onClose={handleBlockedDialogClose}
        onOpenChange={handleBlockedDialogChange}
        open={Boolean(blockedInvoiceData)}
      />

      <NoInvoicingConfirmationDialog
        isSubmitting={isUpdating}
        onCancel={handleCancelNoInvoicing}
        onConfirm={handleConfirmNoInvoicing}
        onOpenChange={handleNoInvoicingDialogChange}
        open={isNoInvoicingDialogOpen}
      />

      {isLoading && <InvoiceDataListSkeleton />}

      {isError && !invoiceData && (
        <ErrorState onRetry={() => refetch()} retryLabel={t('retry')} title={t('errorLoading')} />
      )}

      {!isLoading && invoiceData && (
        <RadioGroup
          className="gap-4"
          disabled={areActionsLocked}
          onValueChange={handleValueChange}
          value={selectedValue}
        >
          <div className="flex items-center gap-2 py-1">
            <RadioGroupItem className="h-4 w-4" id={NO_INVOICING_VALUE} value={NO_INVOICING_VALUE} />
            <label className="text-sm" htmlFor={NO_INVOICING_VALUE}>
              {t('blockInvoicing.noInvoicing')}
            </label>
          </div>

          {invoiceData.length === 0 ? (
            <EmptyState description={t('emptyDescription')} title={t('emptyTitle')} />
          ) : (
            <div className="grid gap-6 grid-cols-[repeat(auto-fill,minmax(335px,1fr))]">
              {invoiceData.map((item) => (
                <InvoiceDataCard
                  areActionsDisabled={areActionsLocked}
                  invoiceData={item}
                  isSelectable
                  isSelected={selectedValue === item.uuid}
                  key={item.uuid}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  showActions={!isInactive}
                />
              ))}
            </div>
          )}
        </RadioGroup>
      )}
    </div>
  );
};
